import React from "react";
import { motion, MotionConfig } from "framer-motion";
import { FONT } from "@/lib/tokens";
import { appleEase } from "@/lib/motion";
import SystemStackDiagram from "@/components/home/SystemStackDiagram";
import SystemToolsPanel from "@/components/home/SystemToolsPanel";

/* ──────────────────────────────────────────────────────────────
   L'integration.

   Titre et sous-titre centres en haut, puis deux blocs cote a
   cote : le schema des trois etages a gauche, les outils et la
   promesse a droite.

   Les deux blocs sont du HTML natif (SystemStackDiagram et
   SystemToolsPanel), plus des images : le texte reste net a tous
   les zooms, il est indexable, et il suit les tokens du site.
   Seuls les logos de marques restent des fichiers.

   Mobile : les deux blocs s'empilent, le schema d'abord.
   ────────────────────────────────────────────────────────────── */

const INK    = "#1D1D1F";
const GREY_1 = "#6E6E73";

const CSS = `
  .tg-eco-section {
    background: #F5F5F7;
    padding: 130px clamp(24px, 5.5vw, 80px) 140px;
    font-family: ${FONT};
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .tg-eco-header {
    text-align: center;
    max-width: 860px;
    margin: 0 auto 72px;
  }

  /* Meme token que les autres labels de l'accueil. */
  .tg-eco-eyebrow {
    font-size: clamp(18px, 2vw, 19.5px);
    font-weight: 600;
    letter-spacing: -0.01em;
    color: #3A3A3C;
    margin: 0 0 20px;
  }

  .tg-eco-title {
    font-size: clamp(30px, 4.6vw, 44px);
    font-weight: 700;
    letter-spacing: -0.03em;
    line-height: 1.15;
    color: ${INK};
    margin: 0 0 20px;
  }

  .tg-eco-sub {
    font-size: clamp(16px, 1.3vw, 18px);
    line-height: 1.55;
    letter-spacing: -0.01em;
    color: ${GREY_1};
    margin: 0;
  }

  /* ── Les deux visuels ── */
  .tg-eco-grid {
    width: min(1160px, 100%);
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: clamp(28px, 4vw, 64px);
    align-items: center;
  }

  /* ── Mobile : on empile, le schema en premier ── */
  @media (max-width: 900px) {
    .tg-eco-section { padding: 88px 22px 96px; }
    .tg-eco-header { margin-bottom: 44px; }
    .tg-eco-grid { grid-template-columns: 1fr; gap: 32px; }
  }
`;

export default function SystemEcosystemSection() {
  const reveal = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.8, ease: appleEase },
  };

  return (
    <MotionConfig reducedMotion="user">
      <style>{CSS}</style>
      <section className="tg-eco-section">
        <div className="tg-eco-header">
          <motion.p className="tg-eco-eyebrow" {...reveal}>
            L&apos;intégration
          </motion.p>
          <motion.h2
            className="tg-eco-title"
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.08 }}
          >
            Un système s&apos;installe dans vos outils.
          </motion.h2>
          <motion.p
            className="tg-eco-sub"
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.16 }}
          >
            Pas besoin de tout changer.
            <br />
            Un système vient se poser sur ce que votre entreprise utilise déjà.
          </motion.p>
        </div>

        <div className="tg-eco-grid">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: appleEase }}
          >
            <SystemStackDiagram />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: appleEase, delay: 0.14 }}
          >
            <SystemToolsPanel />
          </motion.div>
        </div>
      </section>
    </MotionConfig>
  );
}
